import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import type { Bounds, Raster } from '@/modules/Editor/models/raster/Raster'
import { assertImageRaster } from '@/modules/Editor/models/raster/assertImageRaster'
import { assertVideoRaster } from '@/modules/Editor/models/raster/assertVideoRaster'
import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import type { View } from '@/modules/Editor/views/view'
import { isMaskAnnotationEmpty } from '@/modules/Editor/plugins/mask/utils/shared/isMaskAnnotationEmpty'
import { getAnnotationSegmentFromRasterLabel } from '@/modules/Editor/plugins/mask/utils/shared/getAnnotationSegmentFromRasterLabel'
import { isVideoAnnotationData } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { getLabelBounds } from '@/modules/Editor/plugins/mask/utils/shared/getLabelBounds'

type LabelIndex = number
type FrameIndex = number

/**
 * Updates a mask annotation and the corresponding raster, and
 * publish the change to the annotationManager.
 */
export const updateMaskAnnotation = (
  view: View,
  raster: Raster,
  annotation: Annotation,
  bounds?: Bounds,
  frameIndexes?: number[],
  keepInterpolation: boolean = false,
): void => {
  const modifiedFrameIndexes = frameIndexes ?? [view.currentFrameIndex]
  const annotationData = {
    rasterId: raster.id,
  }

  const labelsNotFoundOnFrame: [LabelIndex, FrameIndex][] = []

  modifiedFrameIndexes.forEach((frameIndex) => {
    const labelIndex = raster.getLabelIndexForAnnotationId(annotation.id)

    if (labelIndex === undefined) {
      throw new Error('label not found on raster')
    }

    // only when no bounds are provided, we use getLabelBounds
    const labelBounds = bounds || getLabelBounds(raster, labelIndex, frameIndex)

    if (raster.type === RasterTypes.VIDEO || raster.type === RasterTypes.VOXEL) {
      const videoRaster = assertVideoRaster(raster)
      const videoAnnotationData = annotation.data

      if (!isVideoAnnotationData(videoAnnotationData)) {
        throw new Error('Annotation data for VOXEL or VIDEO raster is invalid')
      }

      const rasterBufferAccessor = videoRaster.getBufferForEdit(frameIndex)
      const labelNotPresentAtFrame = isMaskAnnotationEmpty(
        labelIndex,
        rasterBufferAccessor,
        labelBounds,
      )

      if (!labelNotPresentAtFrame) {
        if (!videoRaster.getFrameBufferAt(frameIndex)) {
          videoRaster.createNewKeyframe(frameIndex)
        }

        // Synchronise the raster layer with the updated label bounds
        videoRaster.setLabelOnKeyframe(labelIndex, frameIndex, labelBounds)

        // We default to false, as expected for any manual change.
        // The only exception is undo/redo, which will use the `keepInterpolation` flag.
        const interpolated = keepInterpolation
          ? videoAnnotationData.frames[frameIndex]?.interpolated
          : false
        videoAnnotationData.frames[frameIndex] = {
          ...annotationData,
          interpolated,
        }
      } else {
        // If the label is not present in this frame, instead of removing it from the raster,
        // we schedule it for removal after all keyframes are processed.
        // This is because removing the label could also remove the entire keyframes in undo/redo
        // operations for multiple frames, and that would make us drop the buffer for it.
        // Removing it after all frames are processed, make sure we only drop the keyframe when needed
        labelsNotFoundOnFrame.push([labelIndex, frameIndex])
      }

      if (!videoAnnotationData.segments) {
        throw new Error('Annotation segments not found on VOXEL or VIDEO raster')
      }
      // We should not rely on mask annotation segments, but the right-hand sidebar does.
      // TODO: we should update the sidebar so that it checks rasterRange instead
      videoAnnotationData.segments[0] = getAnnotationSegmentFromRasterLabel(videoRaster, labelIndex)
      view.annotationManager.updateAnnotationData(annotation, videoAnnotationData)
    } else {
      view.annotationManager.updateAnnotationData(annotation, annotationData)

      const imageRaster = assertImageRaster(raster)

      imageRaster.setBoundsForLabelIndex(labelIndex, labelBounds)
    }
  })

  view.annotationManager.updateAnnotation(annotation, {
    updatedFramesIndices: modifiedFrameIndexes,
  })

  // The data at this point is already correct, and sent to the annotation manager to persist it,
  // but we still have a potential list of labels that were not found on raster frames, that needs
  // to be potentially cleaned up
  if (labelsNotFoundOnFrame.length > 0) {
    if (raster.type === RasterTypes.VIDEO || raster.type === RasterTypes.VOXEL) {
      const videoRaster = assertVideoRaster(raster)

      labelsNotFoundOnFrame.forEach(([labelIndex, frameIndex]) => {
        const rasterBufferAccessor = videoRaster.getBufferForEdit(frameIndex)
        const labelBounds = getLabelBounds(raster, labelIndex, frameIndex)

        if (isMaskAnnotationEmpty(labelIndex, rasterBufferAccessor, labelBounds)) {
          // now it is safe to call delete label on keyframe, as the keyframe will contain all
          // the labels already, and will only be deleted if needed
          videoRaster.deleteLabelOnKeyframes(labelIndex, [frameIndex])
        }
      })

      videoRaster.validateRasterRange()
    }
  }
}
